import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { SelectOption } from '@gainsystems/gains-ui-commons';
import { Subject, takeUntil } from 'rxjs';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { AIAgentService } from '../../services/ai-agent.service';
import { getApiErrorMessage } from '../../services/api-error.helper';
import { DEOProject, MoveToProjectModalData, MoveToProjectModalResult } from '../../models/chat.models';

@Component({
  selector: 'deo-ai-assistant-move-to-project',
  standalone: false,
  templateUrl: './deo-ai-assistant-move-to-project.component.html',
  styleUrls: ['./deo-ai-assistant-move-to-project.component.scss']
})
export class DeoAiAssistantMoveToProjectComponent implements OnInit, OnDestroy {
  modalTitle = 'Move to Project';
  projectOptions: SelectOption[] = [];
  isLoadingProjects = false;

  projectControl = new FormControl<SelectOption | null>(null, [Validators.required]);
  formGroup = new FormGroup({ project: this.projectControl });

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(NZ_MODAL_DATA) private readonly _modalData: MoveToProjectModalData,
    private readonly _modalRef: NzModalRef<DeoAiAssistantMoveToProjectComponent, MoveToProjectModalResult | undefined>,
    private readonly _aiAgentService: AIAgentService,
    private readonly _gainsToastService: GainsToastService
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  get sessionCount(): number {
    return this._modalData?.sessionCount ?? 1;
  }

  onCancel(): void {
    this._modalRef.destroy();
  }

  onSave(): void {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this._modalRef.close({ projectId: (this.projectControl.value?.value as string) ?? null });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private loadProjects(): void {
    this.isLoadingProjects = true;
    this._aiAgentService
      .getProjects()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          this.projectOptions = (response?.data ?? []).map(project => this.mapProjectToOption(project));
          this.preselectCurrentProject();
          this.isLoadingProjects = false;
        },
        error: error => {
          this.projectOptions = [];
          this.isLoadingProjects = false;
          this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to load projects. Please try again.'));
        }
      });
  }

  private mapProjectToOption(project: DEOProject): SelectOption {
    return { title: project.name, value: project.id };
  }

  private preselectCurrentProject(): void {
    const currentProjectId = this._modalData?.currentProjectId;
    if (!currentProjectId) {
      return;
    }

    const matchedOption = this.projectOptions.find(option => option.value === currentProjectId);
    if (matchedOption) {
      this.projectControl.setValue(matchedOption);
    }
  }
}
